import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { Search, Menu } from 'lucide-react'
import SXTIcon from '@/components/icons/sxt-color.svg';
import TheWalrus from '@/components/icons/the-walrus.svg';

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectTrigger,
	SelectContent,
	SelectItem
} from '@/components/ui/select';

import {
	Sheet,
	SheetContent,
	SheetTrigger,
} from "@/components/ui/sheet"


import { LeftNav } from './LeftNav';
import { useApiConfig, useSearch } from '@/hooks/useApi';
import { SelectValue } from '@radix-ui/react-select';

export function Header() {
	const navigate = useNavigate()
	const location = useLocation();
	const { network, setNetwork } = useApiConfig();
	const { mutateAsync: search } = useSearch();

	const [query, setQuery] = useState('')

	const [selectedSection, setSelectedSection] = useState("overview");
	const [navOpen, setNavOpen] = useState<boolean>(false);



	const handleNavSelect = (id: string) => {
		setSelectedSection(id);

		// special consideration for 'overview' which should resolve to '/'
		navigate(id === "overview" ? '/' : `/${id}`);
		setNavOpen(false);
	};

	const handleSearch = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!query.trim()) return

		const searchResult = await search(query);

		if (!searchResult.redirectId) {
			alert('no blob found')
			return;
		}

		navigate(`/blobs/${searchResult.redirectId}`)
	}

	// keep the selected section (page) in sync with the site location
	useEffect(() => {
		const path = location.pathname.replace("/", "");
		setSelectedSection(path === '' ? 'overview' : path);
	}, [location.pathname])


	return (
		<header className="sticky top-0 z-50 w-full">
			<div className="flex flex-col items-center m-4 border-2 border-black bg-[#0C0F1D]/70 backdrop-blur-md px-1 py-2 rounded-sm shadow-[4px_4px_0_0_black] h-47 sm:h-35 lg:h-25 gap-3">
				<div className='flex flex-row items-center gap-4 px-3 py-2 w-full'>
					<div className='lg:hidden'>
						<Sheet open={navOpen} onOpenChange={setNavOpen}>
							<SheetTrigger asChild>
								<Menu className='h-8 w-8 text-white' />
							</SheetTrigger>
							<SheetContent side="left" className="flex flex-col items-center justify-start gap-6 py-4 px-4 w-60 border-2 border-black bg-[#0C0F1D]/70 backdrop-blur-md shadow-[6px_6px_0_0_black] rounded-sm m-3 h-auto">
								<LeftNav
									selectedId={selectedSection}
									onSelect={handleNavSelect}
									className='w-full h-full bg-[#0C0F1D]/70 backdrop-blur-md'
								/>
							</SheetContent>
						</Sheet>
					</div>

					{/* Brand & Navigation */}
					<div className='flex-1 lg:flex-none flex flex-row gap-3 items-center just-start sm:justify-center'>
						<Link to="/" className="flex items-center space-x-2">
							<img src={TheWalrus} className="h-9 w-auto" alt="icon" />
							<div className="flex flex-col gap-1 items-center">
								<div className='flex flex-col items-center justify-center'>
									<span className="text-xl font-bold text-white">Walrus Explorer</span>
									<div className="flex flex-row gap-1 text-[0.5rem] items-center text-white mt-1">
										<span>Powered by Space and Time</span>
										<a href="https://www.spaceandtime.io/" target="_blank" rel="noopener noreferrer">
											<img src={SXTIcon} className="w-3 h-3" alt="icon" />
										</a>
									</div>
								</div>
							</div>
						</Link>
					</div>


					{/* Search */}
					<div className='hidden lg:block lg:flex-1 '>
						<div className="flex flex-1 w-full">
							<div className="relative w-full">
								<form onSubmit={handleSearch} className="flex w-full items-center space-x-2">
									<div className="relative flex-1">
										<Input
											type="search"
											placeholder="Search by blob object ID"
											className="pl-8 border-2 border-black rounded-sm bg-[#37C5B3]/70 backdrop-blur-md text-white placeholder:text-white shadow-[3px_3px_0_0_black]"
											value={query}
											onChange={(e) => setQuery(e.target.value)}
										/>
										<Search className="absolute left-3 top-3 h-4 w-4 text-white" />
									</div>
									<Button type="submit" className="text-white bg-[#A56FF1]/70 border-2 border-black rounded-sm shadow-[2px_2px_0_0_black]"
									>
										Search
									</Button>
								</form>
							</div>
						</div>
					</div>

					{/* NETWORK SELECTOR */}
					<div className='hidden sm:block w-26'>
						<Select value={network} onValueChange={setNetwork}>
							<SelectTrigger
								className="bg-[#A56FF1]/70 text-white border-2 border-black rounded-sm px-3 py-2 shadow-[2px_2px_0_0_black] text-sm flex items-center justify-between"
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent className="border-2 border-black rounded-sm shadow-[2px_2px_0_0_black] bg-[#A56FF1] text-white">
								<SelectItem value="mainnet" className="hover:bg-[#c0a6e9] focus:bg-[#c0a6e9] text-white">
									Mainnet
								</SelectItem>
								<SelectItem value="testnet" className="hover:bg-[#c0a6e9] focus:bg-[#c0a6e9] text-white">
									Testnet
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>
				<div className='sm:hidden flex flex-row justify-end w-full px-3'>
					<div className="flex flex-1 w-full">
						<Select value={network} onValueChange={setNetwork}>
							<SelectTrigger
								className="bg-[#A56FF1]/70 text-white border-2 border-black rounded-sm px-3 py-2 shadow-[2px_2px_0_0_black] text-sm flex items-center justify-between"
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent className="border-2 border-black rounded-sm shadow-[2px_2px_0_0_black] bg-[#A56FF1] text-white">
								<SelectItem value="mainnet" className="hover:bg-[#c0a6e9] focus:bg-[#c0a6e9] text-white">
									Mainnet
								</SelectItem>
								<SelectItem value="testnet" className="hover:bg-[#c0a6e9] focus:bg-[#c0a6e9] text-white">
									Testnet
								</SelectItem>
							</SelectContent>
						</Select>

					</div>
				</div>
				<div className='lg:hidden flex flex-row justify-end w-full px-3'>
					<div className="flex flex-1 w-full">
						<div className="relative w-full">
							<form onSubmit={handleSearch} className="flex w-full items-center space-x-2">
								<div className="relative flex-1">
									<Input
										type="search"
										placeholder="Search by blob object ID"
										className="pl-8 border-2 border-black rounded-sm bg-[#37C5B3]/70 backdrop-blur-md text-white placeholder:text-white shadow-[3px_3px_0_0_black]"
										value={query}
										onChange={(e) => setQuery(e.target.value)}
									/>
									<Search className="absolute left-3 top-3 h-4 w-4 text-white" />
								</div>
								<Button type="submit" className="text-white bg-[#A56FF1]/70 border-2 border-black rounded-sm shadow-[2px_2px_0_0_black]"
								>
									Search
								</Button>
							</form>
						</div>
					</div>
				</div>
			</div>
		</header>
	)
}